import React from 'react';
import {
    Scale,
    FileText,
    Shield,
    AlertCircle,
    UserCheck,
    CreditCard,
    Lock,
    Ban,
    ChevronRight,
    Copyright,
    EyeOff,
    Users,
    ShieldAlert,
} from 'lucide-react';
import { renderLegalContentBlock, slugify } from './legalContent';
import '../styles/LegalDocument.css';

const lastUpdated = "July 8, 2026";

const sections = [
    {
        icon: UserCheck,
        title: "Acceptance of Terms",
        content: `By accessing or using Ufficient's services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing our services.

These terms apply to all users, including without limitation: browsers, customers, merchants, vendors and content contributors.

We reserve the right to update or modify these terms at any time without prior notice. Your continued use of our services following any changes constitutes acceptance of those changes.`
    },
    {
        icon: FileText,
        title: "Service Description",
        content: `Ufficient provides an AI-powered knowledge management platform that includes:

• Document management and organization
• AI-powered search capabilities
• Advanced analytics and reporting
• Integration with existing systems
• Custom deployment options

We reserve the right to:
• Modify or withdraw any part of our services
• Limit or deny access to services
• Change service specifications
• Add or remove features

Any new features that augment or enhance the current service shall be subject to these Terms of Service.`
    },
    {
        icon: Copyright,
        title: "Intellectual Property Rights",
        content: `All software, source code, algorithms, data models, workflows, user interface designs, documentation, and any other materials comprising the Software ("Product Materials") are and shall remain the sole and exclusive property of Ufficient. Except for the limited license expressly granted herein, no right, title, or interest in the Product Materials is transferred to you.

You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the Software solely for your own internal business purposes as expressly authorized under your applicable service agreement with Ufficient. This license does not include the right to sublicense, resell, or grant access to the Software to any party not expressly authorized by Ufficient.`
    },
    {
        icon: Shield,
        title: "User Obligations",
        content: `You agree to:

• Provide accurate and complete registration information
• Maintain the security of your account credentials
• Accept responsibility for all activities under your account
• Notify us immediately of any security breaches
• Comply with all applicable laws and regulations

You agree not to:
• License, sell, or transfer your account
• Use the service for illegal purposes
• Upload malicious code or content
• Attempt to gain unauthorized access
• Interfere with or disrupt services
• Engage in automated data collection without permission
• Copy, reproduce, reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code, underlying structure, algorithms, or trade secrets of the Software
• Create derivative works based on the Software, or use the Software, its outputs, or knowledge gained through its use to design, develop, or assist in developing any product or service that is substantially similar to, or competitive with, the Software
• Provision, authorize, or permit any employee, contractor, or third party — including AI agent developers, automated systems, or bots — to access your account without first ensuring that individual is bound by confidentiality and non-replication obligations at least as protective as those set forth in this Agreement`
    },
    {
        icon: EyeOff,
        title: "Confidentiality of Product Mechanics",
        content: `In the course of using the Software, you and your personnel may observe or become aware of Ufficient's underlying architecture, workflows, data models, prompts, algorithms, business logic, or other proprietary methods ("Product Mechanics"). You agree to treat all Product Mechanics as confidential information belonging to Ufficient and shall not disclose, replicate, or use such information for any purpose outside the scope of your authorized use of the Software.

This obligation survives termination of your account and continues for so long as the relevant Product Mechanics remain confidential and not independently known to the public.`
    },
    {
        icon: Users,
        title: "Third-Party and Contractor Access",
        content: `If you grant any employee, contractor, or third party access to your account or the Software — including individuals engaged for AI agent development, systems integration, or similar technical work — you remain fully responsible for their conduct as if it were your own. You shall ensure any such individual is contractually bound to confidentiality and non-replication obligations no less protective than those in this Agreement before being granted access.

Ufficient may require disclosure of the intended scope of work for any third-party technical personnel granted account access, and may condition continued access on that disclosure.`
    },
    {
        icon: Lock,
        title: "Data Rights and Usage",
        content: `By using our services, you grant Ufficient the right to:

• Store and process your data
• Display and distribute your content within the service
• Use anonymized data for service improvement
• Maintain backups of your information

You retain all rights to your data and may:
• Access your data at any time
• Export your data in standard formats
• Request deletion of your data
• Control sharing and visibility settings

We will handle your data in accordance with our Privacy Policy and applicable data protection laws.`
    },
    {
        icon: CreditCard,
        title: "Payment Terms",
        content: `For paid services:

• Fees are due in advance of service period
• All fees are non-refundable unless otherwise stated
• We may change fees upon 30 days notice
• Taxes are your responsibility where applicable

Subscription terms:
• Billed on a recurring basis
• Auto-renewal unless cancelled
• Pro-rated refunds not provided
• Payment methods must be kept current

Enterprise customers may have separate payment terms as specified in their service agreements.`
    },
    {
        icon: AlertCircle,
        title: "Warranty Disclaimer",
        content: `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED.

We do not warrant that:
• The service will be uninterrupted
• The results will be accurate or reliable
• The quality will meet your expectations
• Errors will be corrected

You assume all risk for any damage that may result from your use of or access to the services.`
    },
    {
        icon: Ban,
        title: "Limitation of Liability",
        content: `IN NO EVENT SHALL UFFICIENT BE LIABLE FOR ANY:

• Indirect or consequential damages
• Lost profits or data
• Business interruption
• Personal injury
• Property damage

Our total liability shall be limited to:
• The amount paid for the service
• Or $500, whichever is greater

Some jurisdictions do not allow the exclusion of certain warranties or limitations, so these limitations may not apply to you.`
    },
    {
        icon: ShieldAlert,
        title: "Monitoring and Enforcement",
        content: `Ufficient reserves the right to monitor account usage, including API calls and access patterns, to ensure compliance with this Agreement. Ufficient may suspend or terminate access immediately, without prior notice, upon reasonable belief that you or your personnel have violated the Intellectual Property Rights, Confidentiality of Product Mechanics, or Third-Party and Contractor Access sections of this Agreement.

You acknowledge that breach of those sections would cause Ufficient irreparable harm for which monetary damages alone are an inadequate remedy, entitling Ufficient to seek injunctive relief in addition to any other remedies available at law or in equity.`
    },
    {
        icon: Scale,
        title: "Dispute Resolution",
        content: `Any dispute arising from these terms shall be resolved through:

1. Informal Negotiation:
• Parties will attempt to resolve disputes informally
• Minimum 30-day negotiation period

2. Mediation:
• If negotiation fails, parties will use mediation
• Costs shared equally between parties

3. Arbitration:
• Final binding arbitration if mediation fails
• Conducted in Toronto, Ontario
• Governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein

Class action waiver applies to all disputes.`
    },
];

const TermsOfServicePage = () => (
    <div className="legal-page">
        <header className="legal-hero">
            <p className="legal-eyebrow">Legal</p>
            <h1>Terms of Service</h1>
            <p className="legal-lede">
                Please read these terms carefully before using our services.
            </p>
            <p className="legal-last-updated">Last updated {lastUpdated}</p>
        </header>

        <div className="legal-shell">
            <nav className="legal-toc" aria-label="On this page">
                {sections.map((section) => {
                    const id = slugify(section.title);
                    return (
                        <a key={id} href={`#${id}`}>
                            {section.title}
                        </a>
                    );
                })}
            </nav>

            <article className="legal-document">
                {sections.map((section, index) => (
                    <section
                        key={index}
                        id={slugify(section.title)}
                        className="legal-section"
                    >
                        <div className="section-header">
                            <section.icon className="section-icon" aria-hidden="true" />
                            <h2>{section.title}</h2>
                        </div>
                        <div className="section-body">
                            {section.content.split('\n\n').map((block, i) =>
                                renderLegalContentBlock(block, i)
                            )}
                        </div>
                    </section>
                ))}

                <aside className="legal-contact">
                    <div>
                        <h2>Need clarification?</h2>
                        <p>Our legal team can help you understand these terms.</p>
                    </div>
                    <a href="/contact" className="contact-button">
                        Contact legal
                        <ChevronRight size={16} />
                    </a>
                </aside>
            </article>
        </div>
    </div>
);

export default TermsOfServicePage;
